import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HandCoins, AlertTriangle, CalendarClock, Wallet, Users, Gem, Landmark, PiggyBank } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { API, money, fmtDate } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { StatusBadge } from "../../components/StatusBadge";
import { DigestCard } from "../../components/DigestCard";
import { useAuth, isManagerUp, isOwnerRole } from "../../lib/auth";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const STATUS_COLORS = { active: "#059669", overdue: "#dc2626", redeemed: "#475569", forfeited: "#d97706" };
const CAT_COLORS = ["#059669", "#1E3A5F", "#d97706", "#7c3aed", "#0891b2", "#dc2626", "#65a30d", "#be185d"];

function Stat({ icon: Icon, label, value, tone = "text-slate-900", testid }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-mono uppercase tracking-wider text-slate-500">{label}</span>
        <Icon className="h-5 w-5 text-emerald-600" />
      </div>
      <div className={`mt-3 font-heading text-2xl font-bold ${tone}`}>{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [range, setRange] = useState(6);

  useEffect(() => {
    API.get("/shop/dashboard", { params: { months: range } }).then((r) => setData(r.data)).catch(() => {});
  }, [range]);

  if (!data) return <p className="text-slate-500" data-testid="dashboard-loading">{t("common.loading")}</p>;

  return (
    <div className="space-y-8" data-testid="shop-dashboard">
      {isManagerUp(user) && <DigestCard managerUp={isManagerUp(user)} isOwner={isOwnerRole(user)} />}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={HandCoins} label={t("dash.active_loans")} value={data.active_loans} testid="stat-active-loans" />
        <Stat icon={AlertTriangle} label={t("dash.overdue")} value={data.overdue_loans} tone="text-red-600" testid="stat-overdue" />
        <Stat icon={CalendarClock} label={t("dash.due_today")} value={data.due_today} tone="text-amber-600" testid="stat-due-today" />
        <Stat icon={Landmark} label={t("dash.capital_out")} value={money(data.capital_out)} testid="stat-capital-out" />
        <Stat icon={PiggyBank} label={t("dash.interest_month")} value={money(data.interest_collected_month)} tone="text-emerald-700" testid="stat-interest-month" />
        <Stat icon={Wallet} label={t("dash.collected_month")} value={money(data.collected_month)} testid="stat-collected-month" />
        <Stat icon={Users} label={t("dash.customers")} value={data.customers} testid="stat-customers" />
        <Stat icon={Gem} label={t("dash.items_pledged")} value={data.items_pledged} testid="stat-items-pledged" />
        <Stat icon={Gem} label={t("dash.items_auction")} value={data.items_auction} tone="text-purple-700" testid="stat-items-auction" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4 lg:col-span-2" data-testid="chart-collections">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-heading text-base font-medium text-slate-900">{t("dash.chart_collections")}</h3>
            <Select value={String(range)} onValueChange={(v) => setRange(Number(v))}>
              <SelectTrigger data-testid="dash-range-select" className="h-7 w-32 text-xs bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="3" data-testid="dash-range-3">{t("dash.range_3")}</SelectItem>
                <SelectItem value="6" data-testid="dash-range-6">{t("dash.range_6")}</SelectItem>
                <SelectItem value="12" data-testid="dash-range-12">{t("dash.range_12")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <ResponsiveContainer width="100%" height={170}>
            <BarChart data={data.monthly_collected} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <XAxis dataKey="month" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={46} />
              <Tooltip formatter={(v) => money(v)} />
              <Legend formatter={(v) => t(v === "capital" ? "fin.capital" : "fin.profit")} wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="capital" stackId="a" fill="#1E3A5F" radius={[0, 0, 0, 0]} />
              <Bar dataKey="interest" stackId="a" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="chart-portfolio">
          <h3 className="font-heading text-base font-medium text-slate-900 mb-2">{t("dash.chart_portfolio")}</h3>
          <ResponsiveContainer width="100%" height={170}>
            <PieChart>
              <Pie data={Object.entries(data.status_breakdown).map(([k, v]) => ({ name: t(`dash.legend_${k}`), key: k, value: v }))}
                dataKey="value" nameKey="name" innerRadius={38} outerRadius={60} paddingAngle={3}>
                {Object.keys(data.status_breakdown).map((k) => <Cell key={k} fill={STATUS_COLORS[k]} />)}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="chart-categories">
        <h3 className="font-heading text-base font-medium text-slate-900 mb-2">{t("dash.chart_categories")}</h3>
        {data.category_breakdown.length === 0 ? (
          <p className="text-sm text-slate-500">{t("common.none")}</p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(110, data.category_breakdown.length * 32)}>
            <BarChart data={data.category_breakdown} layout="vertical" margin={{ top: 0, right: 16, left: 8, bottom: 0 }}>
              <XAxis type="number" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
              <YAxis type="category" dataKey="category" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={95} />
              <Tooltip formatter={(v, name) => (name === "value" ? money(v) : v)} />
              <Bar dataKey="count" name={t("dash.items_pledged")} radius={[0, 4, 4, 0]}>
                {data.category_breakdown.map((c, i) => <Cell key={c.category} fill={CAT_COLORS[i % CAT_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-heading text-lg font-medium text-slate-900 mb-4">{t("dash.due_soon")}</h3>
          {data.due_soon.length === 0 && <p className="text-sm text-slate-500">{t("alerts.empty")}</p>}
          <div className="space-y-3">
            {data.due_soon.map((l) => (
              <button key={l.id} data-testid={`due-soon-${l.loan_number}`} onClick={() => navigate(`/app/loans?open=${l.id}`)}
                className="w-full flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-emerald-400 transition-colors text-left">
                <div>
                  <div className="font-mono text-sm font-semibold text-slate-900">{l.loan_number}</div>
                  <div className="text-xs text-slate-500">{l.customer_name}</div>
                </div>
                <div className="text-right">
                  <StatusBadge status={l.effective_status} />
                  <div className="text-xs text-slate-500 mt-1">{fmtDate(l.due_date)}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-heading text-lg font-medium text-slate-900 mb-4">{t("dash.recent_payments")}</h3>
          {data.recent_payments.length === 0 && <p className="text-sm text-slate-500">{t("common.none")}</p>}
          <div className="space-y-3">
            {data.recent_payments.map((p) => (
              <div key={p.id} data-testid={`recent-payment-${p.id.slice(0, 8)}`} className="flex items-center justify-between p-3 rounded-lg border border-slate-200">
                <div>
                  <div className="font-mono text-sm font-semibold text-slate-900">{p.loan_number}</div>
                  <div className="text-xs text-slate-500">{p.customer_name} · {fmtDate(p.date)}</div>
                </div>
                <div className="font-mono font-semibold text-emerald-700">{money(p.amount)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
